import React, { useContext, useState, useEffect } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Menu,
  Modal,
  Paper,
  Popover,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { Container, Button as MuiButton } from "@mui/material";
import {
  IconChevronDown,
  IconDashboard,
  IconHeart,
  IconHeartFilled,
  IconLogout,
  IconMessage,
  IconSend,
  IconUser,
  IconTrash,
  IconShare,
  IconLanguage,
} from "@tabler/icons-react";

import { NonPaginatedResponse } from "../../Lib/responses";
import { BlogCommentProps, BlogProps, StudentProps } from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";

import { Endpoints } from "../../Lib/endpoints";

import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import { HttpSuccessCodesObj } from "../../Lib/data";
import { DefaultSuccessNotification } from "../../Lib/methods";
import "./styles.scss";
import { AllFatherContext } from "../../App";

const SingleBlog: React.FC = () => {
  const context = useContext(AllFatherContext);
  const { blogID } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isLiked, setIsLiked] = useState(false);

  // Enforce light mode for single blog page
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    if (context && context.setTheme) {
      context.setTheme("light");
    }
    localStorage.setItem("app-theme", "light");
  }, []);
  const [likesCount, setLikesCount] = useState(0);
  const [comments, setComments] = useState<BlogCommentProps[]>([]);

  // Create a ref for the comments section
  const commentsRef = React.useRef<HTMLDivElement>(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [loginPopoverOpen, setLoginPopoverOpen] = useState(false);

  // Function to scroll to comments
  const scrollToComments = () => {
    if (commentsRef.current) {
      commentsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const { data, response, isLoading, error } = usePerformRequest<NonPaginatedResponse<BlogProps>>({
    route: Endpoints.GetSingleBlog(blogID as string).route,
    method: Endpoints.GetSingleBlog(blogID as string).method,
  });

  const {
    data: studentProfile,
    response: studentProfileResponse,
    reloadData: reloadStudentProfile,
    isLoading: isLoadingProfile,
  } = usePerformRequest<NonPaginatedResponse<StudentProps>>({
    method: Endpoints.GetStudentProfile().method,
    route: Endpoints.GetStudentProfile().route,
  });

  // Initialize likes count and check if user has liked when data loads
  React.useEffect(() => {
    if (data?.data) {
      setLikesCount(data.data.likes.length);
      document.title = data.data.title;

      // Set meta tags for SEO and social sharing
      setMetaTags(data.data);

      // Check if current user has liked

      const likes = data.data.likes.map((l) => l.studentID);
      setIsLiked(likes.includes(studentProfile?.data?.id as string));
      setComments(data.data.comments);
    }
  }, [data]);

  const setMetaTags = (blog: BlogProps) => {
    // Set basic meta tags
    updateMetaTag("description", blog.description);
    updateMetaTag("keywords", blog.tags.join(", "));
    updateMetaTag("author", blog.createdBy);

    // Open Graph tags for social media
    updateMetaTag("og:title", blog.title, "property");
    updateMetaTag("og:description", blog.description, "property");
    updateMetaTag("og:image", blog.thumbnail, "property");
    updateMetaTag("og:url", window.location.href, "property");
    updateMetaTag("og:type", "article", "property");
    updateMetaTag("og:site_name", "Emril Student Blog", "property");

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image", "name");
    updateMetaTag("twitter:title", blog.title, "name");
    updateMetaTag("twitter:description", blog.description, "name");
    updateMetaTag("twitter:image", blog.thumbnail, "name");

    // Article specific tags
    updateMetaTag("article:author", blog.createdBy, "property");
    updateMetaTag("article:published_time", new Date(blog.createdAt).toISOString(), "property");
    if (blog.updatedAt) {
      updateMetaTag("article:modified_time", new Date(blog.updatedAt).toISOString(), "property");
    }
    blog.tags.forEach((tag) => {
      addMetaTag("article:tag", tag, "property");
    });
  };

  const updateMetaTag = (name: string, content: string, attribute: string = "name") => {
    let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;

    if (element) {
      element.content = content;
    } else {
      element = document.createElement("meta");
      element.setAttribute(attribute, name);
      element.content = content;
      document.head.appendChild(element);
    }
  };

  const addMetaTag = (name: string, content: string, attribute: string = "name") => {
    const element = document.createElement("meta");
    element.setAttribute(attribute, name);
    element.content = content;
    document.head.appendChild(element);
  };

  // Cleanup meta tags on unmount
  React.useEffect(() => {
    return () => {
      // Reset title to default
      document.title = "Emril Student Blog";

      // Remove custom meta tags
      const metaTagsToRemove = [
        "description",
        "keywords",
        "author",
        "og:title",
        "og:description",
        "og:image",
        "og:url",
        "og:type",
        "og:site_name",
        "twitter:card",
        "twitter:title",
        "twitter:description",
        "twitter:image",
        "article:author",
        "article:published_time",
        "article:modified_time",
      ];

      metaTagsToRemove.forEach((name) => {
        const element = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
        if (element) {
          document.head.removeChild(element);
        }
      });

      // Remove article tags
      const articleTags = document.querySelectorAll('meta[property="article:tag"]');
      articleTags.forEach((tag) => document.head.removeChild(tag));
    };
  }, []);

  const [isTogglingLike, setIsTogglingLike] = useState(false);
  const ToggleLikeBlog = async () => {
    // Check if user is logged in
    if (!studentProfile?.data) {
      setLoginPopoverOpen(true);
      return;
    }

    setIsTogglingLike(true);
    const r = await PerformRequest({
      route: Endpoints.GetToggleLikeBlog(blogID as string).route,
      method: Endpoints.GetToggleLikeBlog(blogID as string).method,
      callback: () => {
        setIsTogglingLike(false);
      },
    });
    setIsTogglingLike(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        setIsLiked(!isLiked);
        setLikesCount((prev) => (isLiked ? prev - 1 : prev + 1));
      }
    }
  };

  const commentForm = useForm({
    initialValues: {
      comment: "",
    },
    validate: {
      comment: (value) => (value.length < 1 ? "Comment is required" : null),
    },
  });
  const [isAddingComment, setAddingComment] = useState(false);
  const AddComment = async () => {
    const { hasErrors } = commentForm.validate();
    if (!hasErrors) {
      setAddingComment(true);
      const r = await PerformRequest<NonPaginatedResponse<BlogCommentProps>>({
        route: Endpoints.GetAddCommentToBlog(blogID as string).route,
        method: Endpoints.GetAddCommentToBlog(blogID as string).method,
        data: {
          content: commentForm.values.comment,
        },
        callback: () => {
          setAddingComment(false);
        },
      });
      setAddingComment(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          commentForm.reset();
          DefaultSuccessNotification("Comment added successfully");
          setComments([...comments, r.data.data]);
        }
      }
    }
  };

  const [isDeletingComment, setDeletingComment] = useState(false);
  const DeleteComment = async (commentID: string) => {
    setDeletingComment(true);
    const r = await PerformRequest<NonPaginatedResponse<BlogCommentProps>>({
      route: Endpoints.GetDeleteCommentFromBlog(blogID as string, commentID).route,
      method: Endpoints.GetDeleteCommentFromBlog(blogID as string, commentID).method,
      callback: () => {
        setDeletingComment(false);
      },
    });
    setDeletingComment(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Comment deleted successfully");
        setComments(comments.filter((c) => c.id !== commentID));
        setDeleteModalOpen(false);
        setCommentToDelete(null);
      }
    }
  };

  const handleDeleteClick = (commentID: string) => {
    setCommentToDelete(commentID);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (commentToDelete) {
      DeleteComment(commentToDelete);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setCommentToDelete(null);
  };

  const handleShareArticle = async () => {
    setIsSharing(true);
    try {
      const currentUrl = window.location.href;

      // Check if the Web Share API is available (mobile browsers)
      if (navigator.share) {
        await navigator.share({
          title: data?.data?.title || "Check out this article",
          text: data?.data?.description || "An interesting article",
          url: currentUrl,
        });
        DefaultSuccessNotification("Article shared successfully!");
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(currentUrl);
        DefaultSuccessNotification("Article link copied to clipboard!");
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") {
        // User cancelled the share dialog
        return;
      }

      // Final fallback for browsers that don't support clipboard API
      try {
        const textArea = document.createElement("textarea");
        textArea.value = window.location.href;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        DefaultSuccessNotification("Article link copied to clipboard!");
      } catch (fallbackError) {}
    } finally {
      setIsSharing(false);
    }
  };

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const getCurrentLanguage = () => {
    switch (i18n.language) {
      case "en":
        return { code: "en", name: "English", flag: "🇬🇧" };
      case "fr":
        return { code: "fr", name: "Français", flag: "🇫🇷" };
      case "pt":
        return { code: "pt", name: "Português", flag: "🇵🇹" };
      case "de":
        return { code: "de", name: "Deutsch", flag: "🇩🇪" };
      default:
        return { code: "en", name: "English", flag: "🇬🇧" };
    }
  };

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
  ];

  return (
    <div className="single-blog-bg">
      {/* Navbar */}
      <nav className="single-blog-navbar">
        <Container maxWidth="lg" className="single-blog-navbar-content">
          <div className="single-blog-navbar-inner">
            <button
              className="single-blog-navbar-logo"
              onClick={() => navigate("/")}
              aria-label="Home"
            >
              <img
                src={context?.assets?.logos?.legacy}
                alt="Logo"
                className="single-blog-logo-img"
              />
            </button>
            <div className="single-blog-navbar-links">
              {studentProfile?.data ? (
                // User is logged in - show back to blogs + menu + language
                <>
                  <MuiButton
                    variant="outlined"
                    className="single-blog-navbar-btn"
                    onClick={() => navigate("/blogs")}
                  >
                    {t("blog.backToBlogs")}
                  </MuiButton>

                  {/* Language Switcher */}
                  <Menu shadow="md" width={180} position="bottom-end">
                    <Menu.Target>
                      <Button
                        variant="light"
                        color="violet"
                        radius="xl"
                        size="sm"
                        rightSection={<IconChevronDown size={12} />}
                        className="language-switcher-btn"
                      >
                        <Group gap="xs" align="center">
                          <IconLanguage size={16} />
                          <Text size="sm" fw={600}>
                            {getCurrentLanguage().flag}
                          </Text>
                        </Group>
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown className="language-dropdown">
                      {languages.map((lang) => (
                        <Menu.Item
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`language-item ${i18n.language === lang.code ? "active" : ""}`}
                        >
                          <Group gap="sm" align="center">
                            <Text>{lang.flag}</Text>
                            <Text size="sm" fw={i18n.language === lang.code ? 700 : 500}>
                              {lang.name}
                            </Text>
                          </Group>
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>

                  <Menu shadow="md" width={200} position="bottom-end">
                    <Menu.Target>
                      <Button
                        variant="light"
                        color="violet"
                        radius="xl"
                        rightSection={<IconChevronDown size={14} />}
                        className="single-blog-navbar-btn user-menu-btn"
                      >
                        <Group gap="xs" align="center">
                          <Avatar size="sm" color="violet">
                            <IconUser size={16} />
                          </Avatar>
                          <Text size="sm" fw={600}>
                            {studentProfile.data.name || t("blog.student")}
                          </Text>
                        </Group>
                      </Button>
                    </Menu.Target>

                    <Menu.Dropdown className="user-menu-dropdown">
                      <Menu.Item
                        leftSection={<IconDashboard size={16} />}
                        onClick={() => navigate("/d")}
                        className="user-menu-item"
                      >
                        {t("blog.dashboard")}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconLogout size={16} />}
                        onClick={() => {
                          localStorage.clear();
                          window.location.href = "/";
                        }}
                        className="user-menu-item logout-item"
                        color="red"
                      >
                        {t("blog.logout")}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </>
              ) : (
                // User is not logged in - show login/signup buttons + language
                <>
                  <MuiButton
                    variant="outlined"
                    className="single-blog-navbar-btn"
                    onClick={() => navigate("/blogs")}
                  >
                    {t("blog.backToBlogs")}
                  </MuiButton>

                  {/* Language Switcher */}
                  <Menu shadow="md" width={180} position="bottom-end">
                    <Menu.Target>
                      <Button
                        variant="light"
                        color="violet"
                        radius="xl"
                        size="sm"
                        rightSection={<IconChevronDown size={12} />}
                        className="language-switcher-btn"
                      >
                        <Group gap="xs" align="center">
                          <IconLanguage size={16} />
                          <Text size="sm" fw={600}>
                            {getCurrentLanguage().flag}
                          </Text>
                        </Group>
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown className="language-dropdown">
                      {languages.map((lang) => (
                        <Menu.Item
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`language-item ${i18n.language === lang.code ? "active" : ""}`}
                        >
                          <Group gap="sm" align="center">
                            <Text>{lang.flag}</Text>
                            <Text size="sm" fw={i18n.language === lang.code ? 700 : 500}>
                              {lang.name}
                            </Text>
                          </Group>
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>

                  <MuiButton
                    variant="outlined"
                    className="single-blog-navbar-btn"
                    onClick={() => navigate("/login")}
                  >
                    {t("auth.login")}
                  </MuiButton>
                  <MuiButton
                    variant="contained"
                    className="single-blog-navbar-btn single-blog-navbar-cta"
                    onClick={() => navigate("/sign-up")}
                  >
                    {t("auth.signUp")}
                  </MuiButton>
                </>
              )}
            </div>
          </div>
        </Container>
      </nav>

      <Container maxWidth="xl">
        <div className="single-blog-container">
          {data?.data && (
            <>
              {/* Blog Header Card */}
              <Card className="blog-header-card" radius="xl" p="xl" mb="xl">
                <Group justify="space-between" align="flex-start" mb="lg">
                  <div style={{ flex: 1 }}>
                    <Text component="h1" className="blog-header-title" mb="md">
                      {data.data.title}
                    </Text>
                    <Text className="blog-header-description" mb="lg">
                      {data.data.description}
                    </Text>

                    {/* Tags */}
                    <Group gap="xs" mb="lg">
                      {data.data.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="light"
                          color="violet"
                          radius="xl"
                          className="blog-tag"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </Group>

                    {/* Author and Date */}
                    <Text size="sm" className="blog-meta" mb="lg">
                      {t("blog.by")} {data.data.createdBy} •{" "}
                      {dayjs(data.data.updatedAt).format("MMMM D, YYYY • h:mm A")}
                    </Text>
                  </div>
                </Group>

                <Divider className="blog-divider" mb="lg" />

                {/* Engagement Stats */}
                <Group justify="space-between" align="center">
                  <Group gap="xl">
                    <Group gap="xs" align="center">
                      <Popover
                        opened={loginPopoverOpen}
                        onClose={() => setLoginPopoverOpen(false)}
                        position="top"
                        width={280}
                        radius="lg"
                        shadow="xl"
                        offset={8}
                        withArrow
                        arrowSize={12}
                      >
                        <Popover.Target>
                          <ActionIcon
                            variant={isLiked ? "filled" : "light"}
                            color="red"
                            size="lg"
                            radius="xl"
                            onClick={ToggleLikeBlog}
                            loading={isTogglingLike}
                            className="like-button"
                          >
                            {isLiked ? <IconHeartFilled size={20} /> : <IconHeart size={20} />}
                          </ActionIcon>
                        </Popover.Target>
                        <Popover.Dropdown className="login-popover">
                          <Stack gap="md" align="center" p="sm">
                            <Text size="sm" fw={600} className="login-popover-title" ta="center">
                              {t("blog.loginToLike")}
                            </Text>
                            <Text size="xs" className="login-popover-subtitle" ta="center">
                              {t("blog.loginSubtitle")}
                            </Text>
                            <Group gap="xs" mt="xs">
                              <Button
                                variant="outline"
                                size="xs"
                                radius="xl"
                                onClick={() => {
                                  setLoginPopoverOpen(false);
                                  navigate("/login");
                                }}
                                className="login-popover-btn"
                              >
                                {t("auth.login")}
                              </Button>
                              <Button
                                variant="gradient"
                                gradient={{
                                  from: "violet",
                                  to: "blue",
                                  deg: 90,
                                }}
                                size="xs"
                                radius="xl"
                                onClick={() => {
                                  setLoginPopoverOpen(false);
                                  navigate("/sign-up");
                                }}
                                className="signup-popover-btn"
                              >
                                {t("auth.signUp")}
                              </Button>
                            </Group>
                          </Stack>
                        </Popover.Dropdown>
                      </Popover>
                      <Text size="sm" fw={600} className="engagement-text">
                        {likesCount} {likesCount === 1 ? t("blog.like") : t("blog.likes")}
                      </Text>
                    </Group>

                    <Group gap="xs" align="center">
                      <ActionIcon
                        variant="light"
                        color="blue"
                        size="lg"
                        radius="xl"
                        className="comment-button"
                        onClick={scrollToComments}
                      >
                        <IconMessage size={20} />
                      </ActionIcon>
                      <Text
                        size="sm"
                        fw={600}
                        className="engagement-text"
                        style={{ cursor: "pointer" }}
                        onClick={scrollToComments}
                      >
                        {comments.length}{" "}
                        {comments.length === 1 ? t("blog.comment") : t("blog.comments")}
                      </Text>
                    </Group>
                  </Group>

                  <Button
                    variant="gradient"
                    gradient={{ from: "violet", to: "blue", deg: 90 }}
                    radius="xl"
                    size="md"
                    className="share-button"
                    onClick={handleShareArticle}
                    leftSection={<IconShare size={16} />}
                    loading={isSharing}
                    disabled={isSharing}
                  >
                    {isSharing ? t("blog.sharing") : t("blog.shareArticle")}
                  </Button>
                </Group>
              </Card>

              <div
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: data.data.content }}
              />

              {/* Comments Section */}
              <Card ref={commentsRef} className="comments-section" radius="xl" p="xl" mt="xl">
                <Text size="xl" fw={700} className="comments-title" mb="lg">
                  {t("blog.commentsCount", { count: comments.length })}
                </Text>

                {/* Add Comment Form */}
                {studentProfile?.data ? (
                  <Paper className="add-comment-form" p="lg" radius="lg" mb="xl">
                    <Group align="flex-start" gap="md">
                      <Avatar size="md" color="violet">
                        <IconUser size={20} />
                      </Avatar>
                      <Stack style={{ flex: 1 }} gap="md">
                        <Textarea
                          {...commentForm.getInputProps("comment")}
                          placeholder={t("blog.commentPlaceholder")}
                          minRows={3}
                          styles={{
                            input: {
                              paddingTop: 13,
                            },
                          }}
                          disabled={isAddingComment}
                          radius="md"
                          className="comment-textarea"
                          autosize
                          maxRows={6}
                        />
                        <Group justify="flex-end">
                          <Button
                            variant="gradient"
                            gradient={{ from: "violet", to: "blue", deg: 90 }}
                            radius="xl"
                            leftSection={<IconSend size={16} />}
                            className="post-comment-btn"
                            onClick={AddComment}
                            loading={isAddingComment}
                          >
                            {t("blog.postComment")}
                          </Button>
                        </Group>
                      </Stack>
                    </Group>
                  </Paper>
                ) : (
                  <Paper className="login-prompt" p="lg" radius="lg" mb="xl">
                    <Group justify="center" align="center" gap="md">
                      <Text className="login-prompt-text">{t("blog.loginToComment")}</Text>
                      <Button
                        variant="outline"
                        color="violet"
                        radius="xl"
                        onClick={() => navigate("/login")}
                        className="login-prompt-btn"
                      >
                        {t("auth.login")}
                      </Button>
                    </Group>
                  </Paper>
                )}

                {/* Comments List */}
                <Stack gap="lg">
                  {comments.length > 0 ? (
                    comments.map((comment, index) => (
                      <Paper key={comment.id || index} className="comment-item" p="lg" radius="lg">
                        <Group align="flex-start" gap="md">
                          <Avatar size="md" color="violet">
                            <IconUser size={20} />
                          </Avatar>
                          <Stack style={{ flex: 1 }} gap="xs">
                            <Group justify="space-between" align="center">
                              <Text fw={600} className="comment-author">
                                {comment.name}
                              </Text>
                              <Group gap="xs" align="center">
                                <Text size="sm" className="comment-date">
                                  {dayjs(comment.createdAt).format("MMMM D, YYYY • h:mm A")}
                                </Text>
                                {/* Only show delete button if user owns the comment */}
                                {studentProfile?.data?.id === comment.studentID && (
                                  <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    size="sm"
                                    radius="xl"
                                    className="delete-comment-btn"
                                    onClick={() => handleDeleteClick(comment.id)}
                                    disabled={isDeletingComment}
                                  >
                                    <IconTrash size={14} />
                                  </ActionIcon>
                                )}
                              </Group>
                            </Group>
                            <Text className="comment-content">{comment.content}</Text>
                          </Stack>
                        </Group>
                      </Paper>
                    ))
                  ) : (
                    <Paper className="no-comments" p="xl" radius="lg">
                      <Stack justify="center" align="center" gap="md">
                        <IconMessage size={48} className="no-comments-icon" />
                        <Text size="lg" fw={600} className="no-comments-title">
                          {t("blog.noComments")}
                        </Text>
                        <Text className="no-comments-subtitle">{t("blog.firstComment")}</Text>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              </Card>

              {/* Delete Confirmation Modal */}
              <Modal
                opened={deleteModalOpen}
                onClose={handleDeleteCancel}
                title=""
                centered
                size="md"
                radius="xl"
                className="delete-modal"
                overlayProps={{
                  backgroundOpacity: 0.55,
                  blur: 3,
                }}
              >
                <Stack gap="lg" align="center" p="md">
                  <div className="delete-modal-icon">
                    <IconTrash size={48} />
                  </div>
                  <Stack gap="sm" align="center">
                    <Text size="xl" fw={700} className="delete-modal-title">
                      {t("blog.deleteComment")}
                    </Text>
                    <Text size="md" className="delete-modal-subtitle" ta="center">
                      {t("blog.deleteConfirmation")}
                    </Text>
                  </Stack>
                  <Group gap="md" mt="md">
                    <Button
                      variant="outline"
                      color="gray"
                      radius="xl"
                      size="md"
                      className="delete-modal-cancel"
                      onClick={handleDeleteCancel}
                      disabled={isDeletingComment}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      variant="filled"
                      color="red"
                      radius="xl"
                      size="md"
                      className="delete-modal-confirm"
                      onClick={handleDeleteConfirm}
                      loading={isDeletingComment}
                    >
                      {t("blog.deleteComment")}
                    </Button>
                  </Group>
                </Stack>
              </Modal>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default SingleBlog;
